import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "@/components/Icon";
import { Badge } from "@/components/Badge";
import Modal from "@/components/Modal";
import { useToast } from "@/context/ToastContext";
import { aiToolsApi } from "@/lib/api";
import { formatDate } from "@/utils/format";

interface SyncProgress {
    current: number;
    total: number | null;
    repo: string;
    phase: "listing" | "syncing";
}

export const Repos = () => {
    const { t } = useTranslation("global");
    const { showSuccess, showError } = useToast();
    const [isOpen, setIsOpen] = useState(false);
    const [isSyncing, setIsSyncing] = useState(false);
    const [isDispatching, setIsDispatching] = useState(false);
    const [isFetching, setIsFetching] = useState(true);
    const [lastSync, setLastSync] = useState<Date | null>(null);
    const [progress, setProgress] = useState<SyncProgress | null>(null);

    const fetchStatus = async (): Promise<void> => {
        try {
            const res = await aiToolsApi.getSyncStatus();
            if (res.last_sync) {
                setLastSync(new Date(res.last_sync));
            }
            if (res.progress) {
                setProgress(res.progress);
            }
            if (res.status === "running") {
                setIsSyncing(true);
            } else {
                setIsSyncing(false);
            }
        } catch (error) {
            console.error("Failed to fetch sync status", error);
        } finally {
            setIsFetching(false);
        }
    };

    useEffect(() => {
        if (!isOpen) return;
        setIsFetching(true);
        fetchStatus();
    }, [isOpen]);

    // Poll while syncing
    useEffect(() => {
        if (!isSyncing) return;
        const interval = setInterval(async () => {
            const res = await aiToolsApi.getSyncStatus();
            if (res.progress) {
                setProgress(res.progress);
            }
            if (res.status !== "running") {
                setIsSyncing(false);
                setProgress(null);
                if (res.last_sync) {
                    setLastSync(new Date(res.last_sync));
                }
                if (res.last_result) {
                    showSuccess(
                        t("tools.rag_sync.success_result", {
                            total: res.last_result.total,
                            updated: res.last_result.updated,
                        })
                    );
                }
                clearInterval(interval);
                setIsOpen(false);
            }
        }, 3000);
        return () => clearInterval(interval);
    }, [isSyncing]);

    const handleSync = async (): Promise<void> => {
        setIsDispatching(true);
        try {
            const res = await aiToolsApi.syncKnowledge();
            showSuccess(res.message);
            setIsSyncing(true);
        } catch (error: unknown) {
            console.error(error);
            const status = (error as { response?: { status?: number } })
                ?.response?.status;
            if (status === 409) {
                showError(t("tools.rag_sync.already_running"));
                setIsSyncing(true);
            } else {
                showError(t("tools.rag_sync.error"));
            }
        } finally {
            setIsDispatching(false);
        }
    };

    const progressPercent =
        progress && progress.total > 0
            ? Math.round((progress.current / progress.total) * 100)
            : 0;

    return (
        <>
            <div
                onClick={() => setIsOpen(true)}
                className="flex flex-col items-center justify-center bg-white p-6 rounded-xl border border-gray-200 shadow-sm cursor-pointer hover:border-primary/50 hover:shadow-md transition-all group"
            >
                <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <Icon
                        value="database"
                        size={24}
                        color="primary"
                        background
                    />
                </div>
                <h2 className="text-sm font-medium text-gray-900 text-center">
                    {t("tools.rag_sync.title")}
                </h2>
                <p className="text-xs text-gray-500 text-center mt-1 dark:text-zinc-400 line-clamp-2">
                    {t("tools.rag_sync.description")}
                </p>
                <div className="mt-3">
                    <Badge variant="neutral" pill={false}>
                        {t("tools.rag_sync.status_badge")}
                    </Badge>
                </div>
            </div>

            <Modal
                isOpen={isOpen}
                onClose={() => setIsOpen(false)}
                title={t("tools.rag_sync.title")}
                maxWidth="md"
                onSubmit={handleSync}
                submitLabel={t("tools.rag_sync.button")}
                submittingLabel={t("tools.rag_sync.syncing")}
                isSubmitting={isDispatching || isSyncing}
                //submitIcon={<Icon value="refresh" size={16} />}
            >
                <div className="flex flex-col items-center justify-center p-2 space-y-6">
                    <div className="flex flex-col items-center justify-center space-y-3 text-center">
                        <Icon
                            value="database"
                            size={32}
                            color="primary"
                            background
                            className="mb-2"
                        />
                        <p className="text-sm text-gray-500 max-w-sm">
                            {t("tools.rag_sync.description")}
                        </p>
                    </div>

                    {isSyncing ? (
                        <div className="w-full space-y-2">
                            <div className="flex justify-between text-xs text-gray-500">
                                <span className="truncate max-w-[200px]">
                                    {progress?.repo ??
                                        t("tools.rag_sync.progress_listing")}
                                </span>
                                {progress && progress.current > 0 && (
                                    <span>
                                        {progress.current}{" "}
                                        {progress.phase === "listing"
                                            ? t("tools.rag_sync.progress_found")
                                            : `/ ${progress.total}`}
                                    </span>
                                )}
                            </div>
                            <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                                {progress?.phase === "syncing" &&
                                progress.total ? (
                                    <div
                                        className="bg-primary h-2 rounded-full transition-all duration-500"
                                        style={{ width: `${progressPercent}%` }}
                                    />
                                ) : (
                                    <div className="bg-primary/40 h-2 rounded-full animate-pulse w-full" />
                                )}
                            </div>
                            {progress?.phase === "syncing" &&
                                progress.total && (
                                    <p className="text-xs text-center text-gray-400">
                                        {progressPercent}%
                                    </p>
                                )}
                        </div>
                    ) : (
                        <div className="text-center">
                            <Badge
                                variant={
                                    isFetching
                                        ? "neutral"
                                        : lastSync
                                          ? "success"
                                          : "neutral"
                                }
                                pill={false}
                            >
                                {isFetching
                                    ? "..."
                                    : lastSync
                                      ? t("tools.rag_sync.status_synced", {
                                            date: formatDate(lastSync),
                                        })
                                      : t("tools.rag_sync.status_never")}
                            </Badge>
                        </div>
                    )}
                </div>
            </Modal>
        </>
    );
};
